<template>
  <v-container
    fluid
    class="align-start"
    :class="
      loading || hasLoadingError || !notifications || !notifications.length
        ? 'fill-height'
        : ''
    "
  >
    <!--heading & filter-->
    <v-container fluid>
      <v-row align="center" justify="space-between">
        <v-col class="text-start" cols="12" sm="6" md="6" lg="12" xl>
          <span class="text-h5 textLight--text font-weight-medium">
            {{ $t("notifications.notifications") }} ({{
              totalNotificationsCount
            }})
          </span>
        </v-col>

        <v-col cols="12" sm="6" md="6" lg="4" xl="3">
          <v-autocomplete
            hide-details
            v-model="notificationType"
            :items="notificationTypes"
            outlined
            dense
            background-color="white"
            :label="$t('notifications.notificationType')"
            class="rounded-lg textLight--text"
            clearable
            :disabled="loading || hasLoadingError || !notifications"
          >
            <template v-slot:selection="{ item }">
              <div class="d-flex justify-start text-capitalize">
                {{ formatNotificationType(item) }}
              </div>
            </template>

            <template v-slot:item="{ item }">
              <div class="d-flex justify-start text-capitalize">
                {{ formatNotificationType(item) }}
              </div>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" sm="12" md="6" lg="3" xl="3">
          <v-text-field
            v-model="search"
            hide-details
            prepend-inner-icon="mdi-magnify"
            color="textLight"
            :placeholder="$t('course.search')"
            outlined
            dense
            background-color="white"
            clearable
            class="shrink rounded-lg"
          />
        </v-col>
        <v-col cols="12" sm="6" md="3" lg xl="2">
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :disabled="loading"
                hide-details
                v-model="formattedStartDate"
                :label="$t('invoice.chooseFromDate')"
                class="rounded-lg"
                outlined
                dense
                background-color="white"
                readonly
                v-bind="attrs"
                v-on="on"
                clearable
              >
                <v-icon slot="append" color="textLight">
                  mdi-calendar-blank-outline
                </v-icon></v-text-field
              >
            </template>

            <v-date-picker
              color="primary"
              v-model="startDate"
              no-title
              @input="startDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" sm="6" md="3" lg xl="2">
          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :disabled="loading"
                hide-details
                class="rounded-lg"
                :label="$t('invoice.chooseToDate')"
                v-model="formattedEndDate"
                outlined
                dense
                background-color="white"
                readonly
                v-bind="attrs"
                v-on="on"
                clearable
              >
                <v-icon slot="append" color="textLight">
                  mdi-calendar-blank-outline
                </v-icon></v-text-field
              >
            </template>

            <v-date-picker
              color="primary"
              v-model="endDate"
              no-title
              @input="endDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
    </v-container>

    <!--loading-->
    <v-container v-if="loading" fluid>
      <v-row>
        <v-col>
          <v-progress-circular indeterminate color="primary" />
        </v-col>
      </v-row>
    </v-container>

    <!--error-->
    <v-container v-else-if="hasLoadingError" fluid>
      <Error />
    </v-container>

    <!--data-->
    <template v-else-if="notifications">
      <v-container v-if="notifications.length" fluid class="align-start">
        <v-row>
          <v-col
            v-for="(data, index) in notifications"
            :key="`notification_detail_${index}`"
            cols="12"
          >
            <NotificationAlert :data="data" :limit-lines="false" />
          </v-col>

          <v-col cols="12" v-if="totalNotificationsCount > pageInput.limit">
            <Pagination :length="totalPages" @onPageSelect="onPagination" />
          </v-col>
        </v-row>
      </v-container>

      <!--empty data-->
      <v-container v-else fluid>
        <v-row>
          <v-col>
            <span class="subtitle-2 textLight--text">
              {{ $t("notifications.noNotifications") }}
            </span>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </v-container>
</template>

<script>
import NotificationAlert from "@/app/core-module/components/NotificationAlert";
import Error from "@/app/core-module/components/Error";
import Pagination from "../components/Pagination.vue";

const defaultPageInput = () => ({
  skip: 0,
  limit: 10,
});

export default {
  name: "NotificationsPage",
  components: { Error, NotificationAlert, Pagination },
  data() {
    return {
      loading: true,
      hasLoadingError: false,
      notifications: null,
      totalNotificationsCount: 0,
      pageInput: defaultPageInput(),
      notificationType: null,
      notificationTypes: [],
      search: null,
      startDate: null,
      endDate: null,
      startDateMenu: false,
      endDateMenu: false,
      filterInputs: {},
      debounceSearch: null,
      totalPages: 0,
    };
  },
  computed: {
    // notificationTypesOld() {
    //   return [
    //     {
    //       key: "TRAINING_NEW_ENROLLMENT",
    //       value: this.$t(
    //         "notifications.notificationTypes.trainingNewEnrollment"
    //       ),
    //     },
    //     {
    //       key: "TRAINING_STARTED",
    //       value: this.$t("notifications.notificationTypes.trainingStart"),
    //     },
    //     {
    //       key: "TRAINING_COMPLETED",
    //       value: this.$t("notifications.notificationTypes.trainingCompletion"),
    //     },
    //     {
    //       key: "TRAINING_QUIZ_PASSED",
    //       value: this.$t("notifications.notificationTypes.trainingQuizPassed"),
    //     },
    //     {
    //       key: "TRAINING_QUIZ_FAILED",
    //       value: this.$t("notifications.notificationTypes.trainingQuizFailed"),
    //     },
    //     {
    //       key: "TRAINING_APPROVAL_REQUEST",
    //       value: this.$t(
    //         "notifications.notificationTypes.trainingApprovalRequest"
    //       ),
    //     },
    //   ];
    // },
    formattedStartDate: {
      get() {
        return this.startDate
          ? this.$moment(this.startDate).format("DD/MM/YYYY")
          : "";
      },
      set(value) {
        this.startDate = value;
      },
    },
    formattedEndDate: {
      get() {
        return this.endDate
          ? this.$moment(this.endDate).format("DD/MM/YYYY")
          : "";
      },
      set(value) {
        this.endDate = value;
      },
    },
  },
  apollo: {
    getNotificationTypes() {
      return {
        query: require("../api/query/getNotificationTypes.gql"),
        result({ data }) {
          this.notificationTypes =
            data?.__type?.enumValues?.map(x => x.name) ?? [];
        },
        update: data => data.__type,
      };
    },
    getNotifications() {
      return {
        query: require("../api/query/getNotifications.gql"),
        variables: { pageInput: this.pageInput },
        result({ data, loading }) {
          if (data?.getNotifications?.notifications) {
            this.notifications = data?.getNotifications?.notifications;
            this.totalNotificationsCount = data?.getNotifications.totalCount;
            let pagesVal = this.totalNotificationsCount / this.pageInput.limit;
            this.totalPages = this.checkNumberIsFloat(pagesVal)
              ? Math.ceil(pagesVal)
              : pagesVal;
          } else {
            this.hasLoadingError = true;
          }

          this.loading = loading;
        },
      };
    },
  },
  watch: {
    search() {
      clearTimeout(this.debounceSearch);
      this.debounceSearch = setTimeout(() => {
        this.apiSearch();
      }, 500);
    },
    startDate() {
      if (this.startDate && this.endDate) {
        this.filterInputs.dateFrom = this.startDate;
        this.filterInputs.dateTo = this.endDate;
      } else if (!this.startDate || !this.endDate) {
        delete this.filterInputs?.dateFrom;
        delete this.filterInputs?.dateTo;
      }
      this.pageInput = defaultPageInput();
      this.reFetchGetNotifications();
    },
    endDate() {
      if (this.startDate && this.endDate) {
        this.filterInputs.dateFrom = this.startDate;
        this.filterInputs.dateTo = this.endDate;
      } else if (!this.startDate || !this.endDate) {
        delete this.filterInputs?.dateFrom;
        delete this.filterInputs?.dateTo;
      }
      this.pageInput = defaultPageInput();
      this.reFetchGetNotifications();
    },
    notificationType() {
      if (!this.notificationType) delete this.filterInputs?.notificationType;
      else this.filterInputs.notificationType = this.notificationType;
      this.pageInput = defaultPageInput();
      this.reFetchGetNotifications();
    },
  },
  methods: {
    checkNumberIsFloat(value) {
      return Number(value) === value && value % 1 !== 0;
    },
    apiSearch() {
      const searchText = this.search?.trim() ?? "";

      if (searchText.length > 0) {
        this.pageInput = defaultPageInput();
        this.filterInputs.search = searchText;
        this.reFetchGetNotifications();
      } else if (searchText.length === 0 && this.filterInputs?.search != null) {
        this.pageInput = defaultPageInput();
        delete this.filterInputs?.search;
        this.reFetchGetNotifications();
      }
    },
    formatNotificationType(item) {
      return item
        ?.replace(new RegExp("TRAINING_", "g"), "COURSE ")
        ?.replace(new RegExp("SUB_ROLE_", "g"), "ROLE ")
        ?.replace(/_/g, " ")
        ?.toLowerCase();
    },
    onPagination(val) {
      if (val > 0) {
        this.pageInput.skip = this.pageInput.limit * (val - 1);
        this.reFetchGetNotifications();
      }
    },
    reFetchGetNotifications() {
      this.loading = true;
      this.$apollo.queries.getNotifications.refetch({
        pageInput: this.pageInput,
        filterInput:
          JSON.stringify(this.filterInputs) !== "{}" ? this.filterInputs : null,
      });
    },
  },
};
</script>
